import ArrowTopRightOnSquareIcon from "@heroicons/react/24/outline/ArrowTopRightOnSquareIcon";
import ChevronDownIcon from "@heroicons/react/24/outline/ChevronDownIcon";
import ChevronRightIcon from "@heroicons/react/24/outline/ChevronRightIcon";
import React, { FC, ReactNode } from "react";
import { useCallback, useState } from "react";
import { NavLink } from "react-router-dom";

import { Box, ButtonBase, Collapse, SvgIcon } from "@mui/material";

import { useSelector } from "../../store";

interface MobileNavItemProps {
  active?: boolean;
  children?: ReactNode;
  depth?: number;
  disabled?: boolean;
  external?: boolean;
  icon?: ReactNode;
  openImmediately?: boolean;
  path?: string;
  title: string;
}

export const MobileNavItem: FC<MobileNavItemProps> = (props) => {
  const {
    active = false,
    children,
    depth = 0,
    external = false,
    icon,
    openImmediately = false,
    path,
    title,
  } = props;
  const [open, setOpen] = useState<boolean>(openImmediately);
  const { organizationId, clusterId } = useSelector((state) => state.current);

  const handleToggle = useCallback((): void => {
    setOpen((prevOpen) => {
      if (["Reports", "Manage"].includes(title)) {
        localStorage.setItem(
          "SideNavOpen_" + title,
          prevOpen ? "false" : "true",
        );
      }
      return !prevOpen;
    });
  }, []);

  if (children) {
    return (
      <li>
        <ButtonBase
          onClick={handleToggle}
          sx={{
            alignItems: "center",
            borderRadius: 1,
            display: "flex",
            fontFamily: (theme) => theme.typography.fontFamily,
            fontSize: 14,
            fontWeight: 500,
            justifyContent: "flex-start",
            px: "6px",
            py: "12px",
            textAlign: "left",
            whiteSpace: "nowrap",
            width: "100%",
          }}
        >
          <Box
            component="span"
            sx={{
              alignItems: "center",
              color: "neutral.400",
              display: "inline-flex",
              flexGrow: 0,
              flexShrink: 0,
              height: 24,
              justifyContent: "center",
              width: 24,
            }}
          >
            {icon}
          </Box>
          <Box
            component="span"
            sx={{
              color: depth === 0 ? "text.primary" : "text.secondary",
              flexGrow: 1,
              fontSize: 14,
              mx: "12px",
              ...(active && {
                color: "primary.main",
              }),
            }}
          >
            {title}
          </Box>
          <SvgIcon sx={{ fontSize: 16 }}>
            {open ? <ChevronDownIcon /> : <ChevronRightIcon />}
          </SvgIcon>
        </ButtonBase>
        <Collapse in={open} sx={{ mt: 0.5 }}>
          {children}
        </Collapse>
      </li>
    );
  }

  // Leaf

  const linkProps = path
    ? external
      ? {
          component: "a",
          href: path,
          target: "_blank",
        }
      : {
          component: NavLink,
          to: path + "?oid=" + organizationId + "&cid=" + clusterId,
        }
    : {};

  return (
    <li>
      <ButtonBase
        sx={{
          alignItems: "center",
          borderRadius: 1,
          display: "flex",
          fontFamily: (theme) => theme.typography.fontFamily,
          fontSize: 14,
          fontWeight: 500,
          justifyContent: "flex-start",
          px: "6px",
          py: "12px",
          textAlign: "left",
          whiteSpace: "nowrap",
          width: "100%",
        }}
        {...linkProps}
      >
        <Box
          component="span"
          sx={{
            alignItems: "center",
            color: "neutral.400",
            display: "inline-flex",
            flexGrow: 0,
            flexShrink: 0,
            height: 24,
            justifyContent: "center",
            width: 24,
          }}
        >
          {icon}
        </Box>
        <Box
          component="span"
          sx={{
            color: depth === 0 ? "text.primary" : "text.secondary",
            flexGrow: 1,
            mx: "12px",
            ...(active && {
              color: "primary.main",
            }),
          }}
        >
          {title}
        </Box>
        {external && (
          <SvgIcon sx={{ fontSize: 18 }}>
            <ArrowTopRightOnSquareIcon />
          </SvgIcon>
        )}
      </ButtonBase>
    </li>
  );
};
